import React, { useState, useMemo } from 'react';
import './IdentificationKeys.css';
import { fetchCharacterData, fetchTaxonData } from '../utils/api';
import { Button, Popover, Col, Row, Carousel, Typography, Image } from 'antd';
import fallbackHabitus from '../assets/images/melaloncha_face_illustration.png'; // fallback if remote missing
import { InfoCircleOutlined } from '@ant-design/icons';
const { Title } = Typography;

const pretty = (s) => s.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

/**
 * Character-state image candidates (prioritize spaces + .png, then underscore variants).
 * Character name must be lowercase in filenames.
 */
const buildCharacterImgCandidates = (genus, characterKey, option) => {
  const charWithSpaces = characterKey.replace(/_/g, ' ').toLowerCase(); // lowercase & spaces
  const charWithUnderscores = characterKey.toLowerCase();               // lowercase & underscores
  const optionText = String(option);

  const candidates = [
    `${genus} ${charWithSpaces} ${optionText}.png`,
    `${genus} ${charWithUnderscores} ${optionText}.png`,
    `${genus} ${charWithSpaces}.png`,
    `${genus} ${charWithUnderscores}.png`,
  ];

  return candidates.map(name => `https://johnhash.me/images/${encodeURIComponent(name)}`);
};

const CharacterStatePreview = ({ genus, characterKey, option, maxWidth = 260 }) => {
  const [index, setIndex] = useState(0);
  const candidates = useMemo(
    () => buildCharacterImgCandidates(genus, characterKey, option),
    [genus, characterKey, option]
  );

  const src = candidates[index];
  if (!src) {
    return (
      <div style={{ maxWidth }}>
        {pretty(characterKey)}: <strong>{option}</strong>
      </div>
    );
  }

  return (
    <div style={{ maxWidth }}>
      <img
        src={src}
        alt={`${genus} – ${pretty(characterKey)}: ${option}`}
        style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 8 }}
        onError={() => setIndex(i => i + 1)}
      />
      <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
        {pretty(characterKey)}: <strong>{option}</strong>
      </div>
    </div>
  );
};

const TaxonImage = ({ filename, alt }) => {
  const [src, setSrc] = useState(
    filename ? `https://johnhash.me/images/${encodeURIComponent(filename)}` : fallbackHabitus
  );

  return (
    <div className="taxon-carousel-image-frame">
      <img
        src={src}
        alt=""
        className="taxon-carousel-image-bg"
        aria-hidden="true"
      />

      <Image
        src={src}
        alt={alt}
        className="taxon-carousel-image"
        fallback={fallbackHabitus}
        preview={{ mask: "View enlarged" }}
        onError={() => setSrc(fallbackHabitus)}
      />
    </div>
  );
};

const IdentificationKeys = () => {
  const [selectedButton, setSelectedButton] = useState(null); // genus
  const [characterData, setCharacterData] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [taxonData, setTaxonData] = useState([]);
  const [filteredTaxonData, setFilteredTaxonData] = useState([]);
  const [selectedTaxa, setSelectedTaxa] = useState([]); // up to 3 ids
  const [hoveredKey, setHoveredKey] = useState(null);

  const handleButtonClick = async (buttonLabel) => {
    setSelectedButton(buttonLabel);
    const characters = await fetchCharacterData(buttonLabel);
    setCharacterData(characters || {});

    const taxonResponse = await fetchTaxonData(buttonLabel, selectedValues);
    setTaxonData(taxonResponse || []);

    const initialSelectedValues = {};
    Object.keys(characters || {}).forEach(key => {
      initialSelectedValues[key] = 'all';
    });
    setSelectedValues(initialSelectedValues);

    setFilteredTaxonData(taxonResponse || []);
  };

  const handleRadioChange = (characterKey, value) => {
    setSelectedValues(prev => {
      const newSelectedValues = { ...prev, [characterKey]: value };

      const filteredData = (taxonData || []).filter(taxon =>
        Object.keys(newSelectedValues).every(key =>
          newSelectedValues[key] === 'all' || taxon[key] === newSelectedValues[key]
        )
      );

      setFilteredTaxonData(filteredData);
      return newSelectedValues;
    });
  };

  const handleTaxonClick = (taxonId) => {
    setSelectedTaxa(prev => {
      const next = [taxonId, ...prev.filter(id => id !== taxonId)];
      return next.slice(0, 3);
    });
  };

  const renderTaxonData = (taxonId) => {
    const taxon = taxonData.find(t => t.id === taxonId);

    if (!taxon) {
      return (
        <Col span={8} key={taxonId}>
          <p>Taxon not found</p>
        </Col>
      );
    }

    const excludedFields = [
      "id","species_id","family","genus","specific_epithet","year",
      "diagnosis","habitus_image","terminalia_lateral_image","terminalia_dorsal_image"
    ];

    return (
      <Col span={8} key={taxonId}>
        <div>
          <h2>{taxon.specific_epithet}</h2>
          
            <Carousel className="taxon-carousel" draggable arrows dots>
              <div>
                <TaxonImage
                  filename={taxon.habitus_image}
                  alt={`${taxon.genus} ${taxon.specific_epithet} – habitus`}
                />
              </div>
              <div>
                <TaxonImage
                  filename={taxon.terminalia_lateral_image}
                  alt={`${taxon.genus} ${taxon.specific_epithet} – terminalia lateral`}
                />
              </div>
              <div>
                <TaxonImage
                  filename={taxon.terminalia_dorsal_image}
                  alt={`${taxon.genus} ${taxon.specific_epithet} – terminalia dorsal`}
                />
              </div>
            </Carousel>
          
          

          <h4>Diagnosis</h4>
          <div>{taxon.diagnosis || "No diagnosis available"}</div>
          <ul>
            {Object.entries(taxon)
              .filter(([key, value]) => !excludedFields.includes(key) && value)
              .map(([key, value]) => (
                <li
                  key={key}
                  className={hoveredKey === key ? "hovered" : ""}
                  onMouseEnter={() => setHoveredKey(key)}
                  onMouseLeave={() => setHoveredKey(null)}
                >
                  <strong>{pretty(key)}:</strong> {String(value)}
                </li>
              ))}
          </ul>
        </div>
      </Col>
    );
  };

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <Title level={2} style={{ marginBottom: 32 }}>
        Identification Keys
      </Title>

      <div className="content">
        <div className="key-instructions">
          <Popover
            placement="bottom"
            trigger={["hover", "click"]}
            content={
              <div className="instructions-popover">
                <ol>
                  <li><strong>Select a genus</strong> to load available diagnostic characters.</li>
                  <li><strong>Toggle character states</strong> to progressively filter matching taxa.</li>
                  <li>
                    <strong>Select up to three taxa</strong> to compare images, diagnoses, and character
                    states side-by-side.
                  </li>
                </ol>
                <p style={{ fontSize: 12, opacity: 0.8 }}>
                  This is a <strong>multi-entry identification key</strong> — characters can be used in any
                  order, and “All” may be selected if a character is unknown or damaged.
                </p>
              </div>
            }
          >
            <span className="instructions-badge">
              <InfoCircleOutlined />
              Learn how to use this identification key
            </span>
          </Popover>
        </div>



        <div className="button-container">
          <Button color="cyan" variant="solid" onClick={() => handleButtonClick('Myriophora')}>Myriophora</Button>

          {process.env.NODE_ENV === 'development' && (
            <>
              <Button
                color="cyan"
                variant="solid"
                onClick={() => handleButtonClick('Pseudomyriophora')}
              >
                Pseudomyriophora
              </Button>

              <Button
                color="cyan"
                variant="solid"
                onClick={() => handleButtonClick('Oreophilophora')}
              >
                Oreophilophora
              </Button>
            </>
          )}

        </div>

        <div className="data-columns">
          {selectedButton && Object.entries(characterData).map(([key, options]) => (
            <div key={key} className="character-group">
              <h4>{pretty(key)}</h4>
              {options.map(option => (
                <label key={option}>
                  <Popover
                    placement="right"
                    title={`${pretty(key)}: ${option}`}
                    content={
                      <CharacterStatePreview
                        genus={selectedButton}
                        characterKey={key}
                        option={option}
                      />
                    }
                  >
                    <input
                      type="radio"
                      name={key}
                      value={option}
                      checked={selectedValues[key] === option}
                      onChange={() => handleRadioChange(key, option)}
                    />
                    {option}
                  </Popover>
                </label>
              ))}
              <label>
                <input
                  type="radio"
                  name={key}
                  value="all"
                  checked={selectedValues[key] === 'all'}
                  onChange={() => handleRadioChange(key, 'all')}
                />
                All
              </label>
            </div>
          ))}
        </div>

        <div className="taxon-list">
          <h3>Matching Taxa</h3>
          <ul>
            {filteredTaxonData.map(taxon => (
              <li key={taxon.id}>
                <Button variant="dashed" onClick={() => handleTaxonClick(taxon.id)}>
                  {taxon.specific_epithet}
                </Button>
              </li>
            ))}
          </ul>
        </div>

        <div className="results-comparator">
          <h3>Taxon Comparator</h3>
          <Row gutter={16}>
            {selectedTaxa.map(taxonId => renderTaxonData(taxonId))}
          </Row>
        </div>
      </div>
    </div>
  );
};

export default IdentificationKeys;
